import { IMAGE_LOADER, ImageLoaderConfig, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { HasRoleDirectiveDirective } from '@/app/common/directives/has-role/has-role-directive.directive';

export type AdminOptionsTabId = 'payouts' | 'tax' | 'moderate' | 'faq' | 'inspired';

@Component({
  selector: 'app-admin-options-nav',
  imports: [RouterLink, HasRoleDirectiveDirective, NgOptimizedImage],
  providers: [
    {
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig) => config.src,
    },
  ],
  templateUrl: './admin-options-nav.html',
  styleUrl: './admin-options-nav.scss',
  host: {
    class: 'flex-0 flex w-full flex-row items-center justify-between',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminOptionsNav {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  /**
   * Access roles allowed to see the Tax tab: GodMode (2), FinanceAdmin (5), PayoutAdmin (7).
   * Deliberately narrower than the rest of admin-options, which is visible to CreatorAdmin —
   * these screens expose individual creators' earnings and taxpayer form status. The real
   * enforcement is FinanceAdminGuard on the API; this only keeps the tab out of sight.
   */
  readonly taxRoles: number[] = [2, 5, 7];

  readonly tabs: { id: AdminOptionsTabId; label: string; path: string }[] = [
    { id: 'payouts', label: 'Payouts', path: '/admin-options/payouts' },
    { id: 'moderate', label: 'Moderate', path: '/admin-options/moderate' },
    { id: 'faq', label: 'FAQ', path: '/admin-options/faq' },
    { id: 'inspired', label: 'Inspired', path: '/admin-options/inspired' },
  ];

  readonly googleAnalyticsIconUrl =
    'https://shout-ugc-assets.s3.eu-west-1.amazonaws.com/other/google_analytics-icon.svg';

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.cdr.markForCheck());
  }

  isTabActive(tabId: AdminOptionsTabId): boolean {
    const url = this.router.url.split('?')[0];
    if (tabId === 'payouts') {
      return url === '/admin-options/payouts' || url === '/admin-options';
    }
    return url === `/admin-options/${tabId}`;
  }

  isSettingsActive(): boolean {
    return this.router.url.split('?')[0].startsWith('/admin-options/settings');
  }

  /**
   * Cancellations is a routed sub-page, not a `@switch` tab, so it matches on a
   * prefix like branding/analytics/settings rather than an exact url.
   */
  isCancellationsActive(): boolean {
    return this.router.url.split('?')[0].startsWith('/admin-options/cancellations');
  }

  isBrandingActive(): boolean {
    return this.router.url.split('?')[0].startsWith('/admin-options/branding');
  }

  isGoogleAnalyticsActive(): boolean {
    return this.router.url.split('?')[0].startsWith('/admin-options/google-analytics');
  }

  getTabClass(tabId: AdminOptionsTabId): string {
    return this.isTabActive(tabId)
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  getSettingsLinkClass(): string {
    return this.isSettingsActive()
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  getCancellationsLinkClass(): string {
    return this.isCancellationsActive()
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  getBrandingLinkClass(): string {
    return this.isBrandingActive()
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  getGoogleAnalyticsLinkClass(): string {
    return this.isGoogleAnalyticsActive()
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }
}
